import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { guid } from '../../common/ambient'
import { isValidPhone } from './AlertSetMemberCard.helpers'
import './InviteGroupMemberModal.less'

function InviteGroupMemberModal ({ smsEnabled, onSave, userActions }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  })
  const [error, setError] = useState('')

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    // Clear error when user starts typing
    if (error) setError('')
  }

  // Determine which invite methods to use based on filled fields and SMS availability
  const determineInviteMethods = (email, phone, smsEnabled) => {
    const methods = []
    if (email && email.trim()) methods.push('email')
    if (phone && phone.trim() && smsEnabled) methods.push('sms')
    return methods
  }

  // Validation
  const validate = () => {
    // Name is required
    if (!formData.name || !formData.name.trim()) {
      setError('Name is required')
      return false
    }

    // At least one of email or phone must be filled
    const hasEmail = formData.email && formData.email.trim()
    const hasPhone = formData.phone && formData.phone.trim()

    if (!hasEmail && !hasPhone) {
      setError('Please provide either an email address or phone number')
      return false
    }

    // If phone is filled, validate format
    if (hasPhone && !isValidPhone(formData.phone)) {
      setError('Phone number must have 10 digits')
      return false
    }

    return true
  }

  const handleSave = () => {
    if (!validate()) {
      return
    }

    const inviteMethods = determineInviteMethods(formData.email, formData.phone, smsEnabled)

    const newMember = {
      _id: guid(),
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      inviteMethods,
      admin: false,
      isNew: true
    }
    onSave(newMember)
  }

  const handleCancel = () => {
    userActions.doModal(null)
  }

  // Check if form is valid for enabling/disabling save button
  const isFormValid = () => {
    const hasName = formData.name && formData.name.trim()
    const hasEmail = formData.email && formData.email.trim()
    const hasPhone = formData.phone && formData.phone.trim()
    const hasContact = hasEmail || hasPhone
    const phoneValid = !hasPhone || isValidPhone(formData.phone)
    return hasName && hasContact && phoneValid
  }

  return (
    <div className='alert-invite-group-member-modal'>
      <h2>Invite User</h2>
      <p className='subtitle'>
        Add a new member to receive heat stress alerts
      </p>

      <div className='form'>
        {error && (
          <div className='error-message'>
            {error}
          </div>
        )}

        <div className='form-line'>
          <label>Name *</label>
          <input
            type='text'
            name='name'
            className='form-control'
            value={formData.name}
            onChange={handleInputChange}
            placeholder='Enter name'
          />
        </div>

        <div className='form-line'>
          <label>Email</label>
          <input
            type='email'
            name='email'
            className='form-control'
            value={formData.email}
            onChange={handleInputChange}
            placeholder='Enter email address'
          />
        </div>

        {smsEnabled && (
            <div className='form-line'>
              <label>Phone</label>
              <input
                type='tel'
                name='phone'
                className='form-control'
                value={formData.phone}
                onChange={handleInputChange}
                placeholder='Enter phone number'
              />
            <div className='helper-text'>
              By pressing Invite I certify that I expressly have permission to send AWN alert text messages to the above number.
            </div>
          </div>
        )}
      </div>

      <div className='modal-actions'>
        <a className='btn btn-secondary' onClick={handleCancel}>
          Cancel
        </a>
        <a
          className={`btn btn-primary ${!isFormValid() ? 'disabled' : ''}`}
          onClick={handleSave}
        >
          Save
        </a>
      </div>
    </div>
  )
}

InviteGroupMemberModal.propTypes = {
  smsEnabled: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  userActions: PropTypes.object.isRequired
}

InviteGroupMemberModal.displayName = 'InviteGroupMemberModal'

export default InviteGroupMemberModal
